import { isOnServer } from "@tm/isomorphic-utils";
import { DEVICE_MODEL_TYPE, HOST } from "@tm/types";
declare global {
  interface Window {
    getHostName: () => string;
    mmt_android_bridge?: any;
    app_bridge?: any;
    dataLayer?: any;
  }
}

export const getDeviceModelType = (): DEVICE_MODEL_TYPE | null => {
  if (isOnServer()) return null;
  if (window.mmt_android_bridge || window.app_bridge) {
    return DEVICE_MODEL_TYPE.APP;
  }
  return DEVICE_MODEL_TYPE.WEB;
};

export function getHostName() {
  if (isOnServer()) {
    return null;
  }
  if (
    typeof window !== "undefined" &&
    typeof window.getHostName === "undefined"
  ) {
    window.getHostName = () => {
      return window.location.hostname;
    };
  }
  return window?.getHostName?.() as string;
}

export function disableEncrytionInWeb() {
  return (
    getDeviceModelType() === DEVICE_MODEL_TYPE.WEB &&
    [HOST.MAKEMYTRIP, HOST.TRIPMONEY_MAKEMYTRIP].includes(getHostName() || "")
  );
}

export const isHostMakemytrip = () => {
  if (isOnServer()) return false;
  const url = new URL(window.location.href);
  return TRIPMONEY_MMT_DOMAIN_NAMES.includes(url.hostname);
};

const getGtmScript = (gtmId: string) => {
  return `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer', '${gtmId}');
  `;
};

export const loadGTM = () => {
  if (isHostMakemytrip() && typeof window !== undefined && !window?.dataLayer) {
    const script = document.createElement("script");
    script.innerHTML = getGtmScript("GTM-NWV3K7C");
    script.defer = true;
    document.head.appendChild(script);
  }
};

export function getUtilOptions(options: any) {
  return disableEncrytionInWeb() ? { ...options, isPiiData: false } : options;
}

export const TRIPMONEY_MMT_DOMAIN_NAMES = [
  "tripmoney.makemytrip.com",
  "insurance.makemytrip.com",
  "www.makemytrip.com",
];

/**
 * Extracts numeric value from a price string and returns it as a number
 * @param priceString - Price string like "₹ 333", "$100.50", "€1,234.56", etc.
 * @returns The numeric value as a number (including decimals), or 0 if parsing fails
 *
 * @example
 * parsePriceToNumber("₹ 333") // returns 333.00
 * parsePriceToNumber("$100.50") // returns 100.50
 * parsePriceToNumber("€1,234.56") // returns 1234.56
 * parsePriceToNumber("invalid") // returns 0
 */
export const parsePriceToNumber = (priceString?: string | null): number => {
  if (!priceString || typeof priceString !== "string") {
    return 0;
  }

  // Remove all non-digit characters except decimal points and commas
  // This handles various currency symbols (₹, $, €, £, etc.) and whitespace
  const cleanedString = priceString.replace(/[^\d.,]/g, "");

  if (!cleanedString) {
    return 0;
  }

  // Handle different decimal separators and thousand separators
  // Common formats: "1,234.56", "1.234,56", "1234.56", "1234"
  let numericString = cleanedString;

  // If there are multiple dots or commas, assume the last one is decimal separator
  const lastDotIndex = numericString.lastIndexOf(".");
  const lastCommaIndex = numericString.lastIndexOf(",");

  if (lastDotIndex > -1 && lastCommaIndex > -1) {
    // Both dot and comma present - the later one is decimal separator
    if (lastDotIndex > lastCommaIndex) {
      // Dot is decimal separator, remove commas
      numericString = numericString.replace(/,/g, "");
    } else {
      // Comma is decimal separator, replace with dot and remove other dots
      numericString =
        numericString.substring(0, lastCommaIndex).replace(/\./g, "") +
        "." +
        numericString.substring(lastCommaIndex + 1);
    }
  } else if (
    lastCommaIndex > -1 &&
    numericString.indexOf(",") !== lastCommaIndex
  ) {
    // Multiple commas - likely thousand separators, remove all
    numericString = numericString.replace(/,/g, "");
  } else if (lastDotIndex > -1 && numericString.indexOf(".") !== lastDotIndex) {
    // Multiple dots - likely thousand separators, remove all except last
    numericString =
      numericString.substring(0, lastDotIndex).replace(/\./g, "") +
      numericString.substring(lastDotIndex);
  }

  // Parse to float and return the full numeric value
  const numericValue = parseFloat(numericString);

  if (isNaN(numericValue)) {
    return 0;
  }

  // Return as double (floating-point number)
  return numericValue;
};
